/* eslint-disable */

import React, { useState } from 'react'
import { RefreshCw, FileCheck2, ArrowLeftRight, ClipboardList } from 'lucide-react'
import { Button } from '../../Components/ui/button'
import { Card, CardContent } from '../../Components/ui/card'
import { Input } from '../../Components/ui/input'
import { Label } from '../../Components/ui/label'
import { post } from '../../http/restservice'
import env from '../../env/env'
import { toast } from 'sonner'
import { cn } from '../../lib/utils'

const DAY_PRESETS = ['7', '15', '30', '90']

const RefreshData = () => {
  const [days, setDays] = useState('30')
  const [loading, setLoading] = useState({
    allotment: false,
    redemption: false,
    order: false,
  })

  const refreshAllotment = async () => {
    setLoading((prev) => ({ ...prev, allotment: true }))
    try {
      const response = await post(
        `${env.API_URL}mutualfundservice/api/mutualFund/newAllotmentStatement/dataRefresh`,
        { days },
      )

      if (response?.isError) {
        toast.error('Failed to refresh allotment data')
      }

      if (!response?.isError && response?.status === 'SUCCESS') {
        toast.success(response?.message)
      }
    } catch (error) {
      alert('Failed to refresh allotment data')
    } finally {
      setLoading((prev) => ({ ...prev, allotment: false }))
    }
  }

  const refreshRedemption = async () => {
    setLoading((prev) => ({ ...prev, redemption: true }))
    try {
      const response = await post(
        `${env.API_URL}mutualfundservice/api/mutualFund/redemptionStatus/dataRefresh`,
        {
          days,
        },
      )

      if (response?.isError) {
        toast.error('Failed to refresh redemption data')
      }

      if (!response?.isError && response?.status === 'SUCCESS') {
        toast.success(response?.message)
      }
    } catch (error) {
      toast.error('Failed to refresh redemption data')
    } finally {
      setLoading((prev) => ({ ...prev, redemption: false }))
    }
  }

  const refreshOrder = async () => {
    setLoading((prev) => ({ ...prev, order: true }))
    try {
      const response = await post(
        `${env.API_URL}mutualfundservice/api/mutualFund/orderStatus/dataRefresh`,
        {
          days,
        },
      )

      if (response?.isError) {
        toast.error('Failed to refresh order data')
      }

      if (!response?.isError && response?.status === 'SUCCESS') {
        toast.success(response?.message)
      }
    } catch (error) {
      alert('Failed to refresh order data')
    } finally {
      setLoading((prev) => ({ ...prev, order: false }))
    }
  }

  const actions = [
    {
      key: 'allotment',
      title: 'Refresh Allotment',
      description: 'Sync allotment statement data for the selected period.',
      icon: FileCheck2,
      onClick: refreshAllotment,
      loadingLabel: 'Refreshing Allotment...',
    },
    {
      key: 'redemption',
      title: 'Refresh Redemption',
      description: 'Sync redemption status data for the selected period.',
      icon: ArrowLeftRight,
      onClick: refreshRedemption,
      loadingLabel: 'Refreshing Redemption...',
    },
    {
      key: 'order',
      title: 'Refresh Orders',
      description: 'Sync order status data for the selected period.',
      icon: ClipboardList,
      onClick: refreshOrder,
      loadingLabel: 'Refreshing Orders...',
    },
  ]

  const isAnyLoading = loading.allotment || loading.redemption || loading.order

  return (
    <div className='mx-auto w-full max-w-none px-4 pb-8 pt-4 md:px-6 lg:px-8'>
      <div className='mb-5 md:mb-6'>
        <p className='mb-1 text-xs font-medium uppercase tracking-[0.08em] text-[var(--fb-text-muted)]'>
          Admin tools
        </p>
        <h1 className='font-IBM text-2xl font-bold tracking-tight text-[var(--fb-text-strong)] md:text-3xl'>
          Refresh Data
        </h1>
        <p className='mt-1 text-sm text-[var(--fb-text-muted)]'>
          Refresh allotment, redemption, and order data for a chosen lookback window.
        </p>
      </div>

      <Card className='w-full overflow-hidden border border-[var(--fb-border)] bg-[var(--fb-surface)] shadow-[var(--fb-shadow-sm)] lg:rounded-3xl'>
        <CardContent className='p-5 md:p-8 lg:p-10'>
          <div className='grid w-full gap-6 lg:grid-cols-2 lg:items-stretch lg:gap-10'>
            <div className='flex h-full flex-col space-y-5'>
              <div className='flex flex-col items-center gap-3 text-center lg:items-start lg:text-left'>
                <div className='flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--fb-accent-soft)] text-[var(--fb-accent-text)]'>
                  <RefreshCw className='h-7 w-7' aria-hidden='true' />
                </div>
                <div>
                  <p className='text-xs font-medium uppercase tracking-[0.08em] text-[var(--fb-text-muted)]'>
                    Lookback period
                  </p>
                  <p className='mt-1 text-sm text-[var(--fb-text-secondary)]'>
                    Choose how many days of data to refresh.
                  </p>
                </div>
              </div>

              <div className='flex flex-1 flex-col justify-center rounded-2xl border border-[var(--fb-border)] bg-[var(--fb-surface-muted)] px-5 py-6 text-center lg:px-8 lg:py-10 lg:text-left'>
                <div className='font-IBM text-3xl font-bold tracking-tight text-[var(--fb-text-strong)] md:text-4xl lg:text-5xl'>
                  {days || '—'}{' '}
                  <span className='text-2xl font-semibold text-[var(--fb-text-muted)] md:text-3xl'>
                    days
                  </span>
                </div>
                <div className='mt-1 text-sm text-[var(--fb-text-muted)]'>
                  Data refresh window
                </div>

                <div className='mt-5 space-y-3'>
                  <Label
                    htmlFor='days'
                    className='text-sm font-medium text-[var(--fb-text)]'
                  >
                    Number of Days
                  </Label>
                  <Input
                    id='days'
                    type='number'
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                    min={1}
                    max={365}
                    disabled={isAnyLoading}
                    className='h-11 rounded-xl border-[var(--fb-border)] bg-[var(--fb-surface)] font-semibold text-[var(--fb-text-strong)]'
                  />
                  <div className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
                    {DAY_PRESETS.map((preset) => {
                      const isSelected = String(days) === preset
                      return (
                        <button
                          key={preset}
                          type='button'
                          disabled={isAnyLoading}
                          onClick={() => setDays(preset)}
                          className={cn(
                            'fabits-text-ui rounded-xl border px-3 py-2 text-sm font-semibold transition-colors',
                            isSelected
                              ? 'border-[var(--fb-accent)] bg-[var(--fb-accent-soft)] text-[var(--fb-accent-text)]'
                              : 'border-[var(--fb-border)] bg-[var(--fb-surface)] text-[var(--fb-text)] hover:bg-[var(--fb-surface-soft)]',
                          )}
                        >
                          {preset}d
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className='flex h-full w-full flex-col space-y-4 lg:space-y-5'>
              {actions.map(({ key, title, description, icon: Icon, onClick, loadingLabel }) => {
                const isLoading = loading[key]
                return (
                  <div
                    key={key}
                    className='rounded-2xl border border-[var(--fb-border)] p-4 md:p-5'
                  >
                    <div className='mb-4 flex items-start gap-3'>
                      <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--fb-surface-muted)] text-[var(--fb-accent-text)]'>
                        <Icon className='h-5 w-5' aria-hidden='true' />
                      </div>
                      <div>
                        <p className='text-sm font-semibold text-[var(--fb-text-strong)]'>
                          {title}
                        </p>
                        <p className='mt-0.5 text-sm text-[var(--fb-text-muted)]'>{description}</p>
                      </div>
                    </div>
                    <Button
                      onClick={onClick}
                      disabled={isLoading || !days}
                      className='h-11 w-full rounded-xl bg-[var(--fb-accent)] text-sm font-semibold text-white hover:bg-[var(--fb-accent-hover)] disabled:bg-[var(--fb-accent)] disabled:opacity-50'
                    >
                      {isLoading ? loadingLabel : title}
                    </Button>
                  </div>
                )
              })}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default RefreshData
